import { Injectable } from '@angular/core';
import { HierarchyObject, HierarchyDataRow, HierarchyNode, HierarchyReportData, HierarchyColumn } from '../interfaces/hierarchy.interface';

@Injectable({
  providedIn: 'root'
})
export class HierarchyService {

  /**
   * Builds a hierarchical structure from flat data with hierarchy objects
   * @param data Array of data rows containing hierarchyObject
   * @param columns Array of column definitions
   * @returns HierarchyReportData with processed hierarchy
   */
  buildHierarchy(data: HierarchyDataRow[], columns: HierarchyColumn[]): HierarchyReportData {
    if (!data || data.length === 0) {
      return { nodes: [], rootNodes: [], totalRecords: 0, maxLevel: 0 };
    }

    // Create arrays for nodes
    const nodeMap = new Map<string, HierarchyNode>();
    const rootNodes: HierarchyNode[] = [];
    let maxLevel = 0;

    // First pass: Create all nodes
    // Store both aggregated data and individual data for parents
    const individualDataMap = new Map<string, any>();
    
    data.forEach((row, index) => {
      const hierarchyObj = row.hierarchyObject;
      if (!hierarchyObj) return;

      const nodeId = String(hierarchyObj.id);
      const hasChildren = hierarchyObj.childUserIds && hierarchyObj.childUserIds.length > 0;
      
      // If node already exists in map
      if (nodeMap.has(nodeId)) {
        // This might be the individual data for a parent node
        console.log(`🔄 Found additional row for ${hierarchyObj.displayName} (ID: ${nodeId})`);
        console.log('  Existing data:', nodeMap.get(nodeId)?.data);
        console.log('  New data:', row);
        
        // If this node has children, store this as individual data
        if (hasChildren) {
          individualDataMap.set(nodeId, row);
          console.log('  → Stored as individual data for parent');
        }
        return;
      }

      const node: HierarchyNode = {
        id: hierarchyObj.id,
        displayName: hierarchyObj.displayName,
        reportingTo: hierarchyObj.reportingTo,
        level: hierarchyObj.level,
        children: [],
        data: row,
        calculatedSums: {},
        isLeaf: !hasChildren
      };
      
      // Use hierarchyObject.id as string key for lookup
      nodeMap.set(nodeId, node);
      maxLevel = Math.max(maxLevel, node.level);
    });

    // Attach individual data to nodes that have it
    individualDataMap.forEach((individualData, nodeId) => {
      const node = nodeMap.get(nodeId);
      if (node) {
        node.individualData = individualData;
        console.log(`✅ Attached individual data to ${node.displayName}`);
      }
    });

    // Second pass: Build parent-child relationships
    const allNodes = Array.from(nodeMap.values());
    allNodes.forEach(node => {
      if (node.reportingTo === 0 || node.reportingTo === null) {
        // Root node
        rootNodes.push(node);
      } else {
        // Find parent and add as child using string key
        const parent = nodeMap.get(String(node.reportingTo));
        if (parent) {
          parent.children.push(node);
        } else {
          // If parent not found, treat as root node
          rootNodes.push(node);
        }
      }
    });

    console.log(`✅ Hierarchy built: ${allNodes.length} unique nodes, ${rootNodes.length} root nodes`);

    return {
      nodes: allNodes,
      rootNodes,
      totalRecords: data.length,
      maxLevel
    };
  }


  /**
   * Flattens hierarchy for display in table format
   * @param hierarchyData The hierarchy data
   * @param columns Column definitions
   * @returns Flattened array for table display
   */
  flattenHierarchyForTable(hierarchyData: HierarchyReportData, columns: HierarchyColumn[]): any[] {
    const flattened: any[] = [];
    this.flattenAllNodesAsChildren(hierarchyData.rootNodes, flattened, columns, 0);
    return flattened;
  }

  /**
   * Flattens all nodes to show each parent as a child record at first level
   * @param nodes Array of nodes to flatten
   * @param result Array to store flattened results
   * @param columns Column definitions
   * @param level Current hierarchy level
   */
  private flattenAllNodesAsChildren(nodes: HierarchyNode[], result: any[], columns: HierarchyColumn[], level: number): void {
    nodes.forEach(node => {
      // Create row for this node with actual hierarchy level
      const row: any = {
        _hierarchyLevel: node.level, // Use actual level for proper indentation
        _hierarchyId: node.id,
        _hierarchyName: node.displayName,
        _isLeaf: node.isLeaf,
        _hasChildren: node.children.length > 0,
        _hierarchyNode: node,
        _originalLevel: level, // Keep track of original level for reference
        _parentId: node.reportingTo,
        _isParent: node.children.length > 0
      };

      // Add original data by key names
      Object.keys(node.data).forEach(key => {
        if (key !== 'hierarchyObject') {
          row[key] = node.data[key];
        }
      });

      // Also add by column index for easier access
      columns.forEach((column, index) => {
        if (node.data[column.columnId] !== undefined) {
          row[index] = node.data[column.columnId];
        } else if (node.data[index] !== undefined) {
          row[index] = node.data[index];
        }
      });

      result.push(row);

      // Recursively add children (but they will also be shown at level 0)
      if (node.children.length > 0) {
        this.flattenAllNodesAsChildren(node.children, result, columns, level + 1);
      }
    });
  }

  /**
   * Recursively flattens nodes for table display
   * @param nodes Array of nodes to flatten
   * @param result Array to store flattened results
   * @param columns Column definitions
   * @param level Current hierarchy level
   */
  private flattenNodes(nodes: HierarchyNode[], result: any[], columns: HierarchyColumn[], level: number): void {
    nodes.forEach(node => {
      // Create row for this node
      const row: any = {
        _hierarchyLevel: level,
        _hierarchyId: node.id,
        _hierarchyName: node.displayName,
        _isLeaf: node.isLeaf,
        _hasChildren: node.children.length > 0,
        _hierarchyNode: node
      };

      // Add original data
      Object.keys(node.data).forEach(key => {
        if (key !== 'hierarchyObject') {
          row[key] = node.data[key];
        }
      });

      result.push(row);

      // Recursively add children
      if (node.children.length > 0) {
        this.flattenNodes(node.children, result, columns, level + 1);
      }
    });
  }

  /**
   * Gets total sums for all numeric columns across the entire hierarchy
   * @param hierarchyData The hierarchy data
   * @param columns Column definitions
   * @returns Object with total sums for each column
   */
  getTotalSums(hierarchyData: HierarchyReportData, columns: HierarchyColumn[]): { [key: string]: number } {
    return {};
  }

  /**
   * Finds a node by ID in the hierarchy
   * @param hierarchyData The hierarchy data
   * @param nodeId The ID to search for
   * @returns The found node or null
   */
  findNodeById(hierarchyData: HierarchyReportData, nodeId: number): HierarchyNode | null {
    for (const node of hierarchyData.nodes) {
      if (node.id === nodeId) {
        return node;
      }
    }
    return null;
  }

  /**
   * Gets all leaf nodes (nodes with no children)
   * @param hierarchyData The hierarchy data
   * @returns Array of leaf nodes
   */
  getLeafNodes(hierarchyData: HierarchyReportData): HierarchyNode[] {
    return hierarchyData.nodes.filter(node => node.isLeaf);
  }

  /**
   * Gets all nodes at a specific level
   * @param hierarchyData The hierarchy data
   * @param level The level to get nodes for
   * @returns Array of nodes at the specified level
   */
  getNodesAtLevel(hierarchyData: HierarchyReportData, level: number): HierarchyNode[] {
    return hierarchyData.nodes.filter(node => node.level === level);
  }
}
